/**
 * Godot's CSG normal rule, which every CSG builder in this folder finishes with. A smooth
 * face's vertex takes the normalised sum of the unit plane normals of every smooth face at that
 * exact position, and a flat face's vertex takes its own plane normal. Neither `flatShading`
 * nor `computeVertexNormals()` gives this.
 *
 * Derived from Godot Engine (`modules/csg/csg_shape.cpp`, `CSGShape3D::update_shape`,
 * and `core/math/plane.h`, `Plane(p_point1, p_point2, p_point3)`), used under the MIT
 * licence:
 *
 *   Copyright (c) 2014-present Godot Engine contributors (see AUTHORS.md).
 *   Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 *
 *   Permission is hereby granted, free of charge, to any person obtaining
 *   a copy of this software and associated documentation files (the
 *   "Software"), to deal in the Software without restriction, including
 *   without limitation the rights to use, copy, modify, merge, publish,
 *   distribute, sublicense, and/or sell copies of the Software, and to
 *   permit persons to whom the Software is furnished to do so, subject to
 *   the following conditions:
 *
 *   The above copyright notice and this permission notice shall be
 *   included in all copies or substantial portions of the Software.
 *
 *   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 *   EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 *   MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 *   IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 *   CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 *   TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 *   SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * See THIRD-PARTY-NOTICES.md.
 */
import * as THREE from 'three';
import { isCollapsed, weldCorners } from './brushManifold';
import { bottomUpV } from '../../../godot/math/uv';
/**
 * `Plane(v0, v1, v2)` with Godot's default `CLOCKWISE` direction:
 * `normal = ((v0 - v2) cross (v0 - v1)).normalized()`.
 *
 * This is the negation of the conventional CCW `(v1 - v0) cross (v2 - v0)`. Reading it the usual
 * way inverts every normal in every CSG mesh.
 */
function planeNormal(positions, base, target, a, b) {
    a.set(positions[base] - positions[base + 6], positions[base + 1] - positions[base + 7], positions[base + 2] - positions[base + 8]);
    b.set(positions[base] - positions[base + 3], positions[base + 1] - positions[base + 4], positions[base + 2] - positions[base + 5]);
    return target.crossVectors(a, b).normalize();
}
/**
 * Turn a face soup into a renderable, boolean-ready `BufferGeometry`.
 *
 * The result is non-indexed and carries `position`, `normal` and `uv`, which is exactly
 * the attribute set `three-bvh-csg` keeps by default; a brush missing one has that
 * channel trimmed out of the boolean result.
 */
export function applyCsgNormals(soup) {
    // A flat face reads no weld, which a brush closed by construction never tests.
    return shade(soup, weldCorners(soup.positions, (triangle) => soup.smooth[triangle] === true));
}
/** `applyCsgNormals` over a weld of every corner, which a builder shares with its manifold test. */
export function shadeCsgFaces(soup) {
    const welds = weldCorners(soup.positions);
    return { geometry: shade(soup, welds), welds };
}
/**
 * The brush of a shape that builds nothing.
 *
 * @canonical An empty CSG brush
 */
export function emptyBrush() {
    return shadeCsgFaces({ positions: new Float32Array(0), uvs: new Float32Array(0), smooth: [] });
}
function shade(soup, welds) {
    const { positions, uvs, smooth, invert } = soup;
    const triangles = Math.floor(positions.length / 9);
    // Pass 1: sum unit plane normals per welded position, smooth faces only. Position, not index,
    // is the key: three gives a cone apex nine radial normals where Godot collapses them to one.
    // The sum is unweighted, so a small face pulls on a vertex as hard as a large one.
    const accumulated = new Map();
    const plane = new THREE.Vector3();
    const edgeA = new THREE.Vector3();
    const edgeB = new THREE.Vector3();
    for (let t = 0; t < triangles; t++) {
        if (!smooth[t])
            continue;
        if (isCollapsed(welds, t))
            continue;
        planeNormal(positions, t * 9, plane, edgeA, edgeB);
        for (let j = 0; j < 3; j++) {
            const key = welds[t * 3 + j];
            const existing = accumulated.get(key);
            if (existing)
                existing.add(plane);
            else
                accumulated.set(key, plane.clone());
        }
    }
    // Pass 2: emit, applying the smooth lookup and the invert swap.
    const outPositions = new Float32Array(triangles * 9);
    const outNormals = new Float32Array(triangles * 9);
    const outUvs = new Float32Array(triangles * 6);
    const normal = new THREE.Vector3();
    const flipped = invert === true;
    // Two swaps compose here, and they cancel. Godot does `int order[3] = {0,1,2}; if (invert) SWAP(order[1],
    // order[2]);` and writes source vertex j into slot order[j]. Godot's front faces also wind
    // clockwise, where three.js culls clockwise, so Godot's order would render each solid as its
    // interior. The normals are supplied explicitly, so this is purely a winding conversion.
    const order = flipped ? [0, 1, 2] : [0, 2, 1];
    for (let t = 0; t < triangles; t++) {
        const base = t * 9;
        const uvBase = t * 6;
        planeNormal(positions, base, plane, edgeA, edgeB);
        for (let j = 0; j < 3; j++) {
            normal.copy(plane);
            if (smooth[t]) {
                const sum = accumulated.get(welds[t * 3 + j]);
                // Godot normalizes the accumulated sum in place. A sum of exactly zero (two
                // faces cancelling on a zero-thickness sheet) would leave a black (0,0,0)
                // normal there; we keep the face's own plane normal instead, which is the
                // same value every non-smooth face at that position already gets.
                if (sum && sum.lengthSq() > 0)
                    normal.copy(sum).normalize();
            }
            if (flipped)
                normal.negate();
            const dst = base + order[j] * 3;
            outPositions[dst] = positions[base + j * 3];
            outPositions[dst + 1] = positions[base + j * 3 + 1];
            outPositions[dst + 2] = positions[base + j * 3 + 2];
            outNormals[dst] = normal.x;
            outNormals[dst + 1] = normal.y;
            outNormals[dst + 2] = normal.z;
            const uvDst = uvBase + order[j] * 2;
            outUvs[uvDst] = uvs[uvBase + j * 2] ?? 0;
            outUvs[uvDst + 1] = bottomUpV(uvs[uvBase + j * 2 + 1] ?? 0);
        }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(outPositions, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(outNormals, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(outUvs, 2));
    return geometry;
}
//# sourceMappingURL=smoothNormals.js.map